/**
 * A stat card — list engine v2's "the list is the dashboard".
 *
 * Four displays over one shape: a coloured dot and a label, one big number,
 * a line of context, and a footnote saying what the number actually is. The
 * footnote is not decoration — a dashboard where you cannot tell what a figure
 * counts is a dashboard people quote at each other and disagree about.
 *
 * The charts are inline SVG rather than a chart library. Plan §2.1 puts
 * `recharts` in Reporting and nowhere else, and these are sixty pixels of
 * sparkline: a library here would be three hundred kilobytes to draw a
 * polyline, and it would bring its own opinions about colour into a product
 * whose whole point is one palette.
 */
import type { CardDefinition, CardResult, FieldIcon } from '@rowted/shared'
import { CARD_PERIOD_LABELS } from '@rowted/shared'
import { Card } from './primitives.js'
import { IconMore, IconPlus } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatCompact, formatNumber } from '../lib/format.js'
import type { CardDensity } from '../lib/use-strip.js'

export interface StatCardProps {
  definition: CardDefinition
  result: CardResult | null
  /** Money or a count — decides whether the number wears a `$`. */
  unit?: 'money' | 'number'
  /** How the card describes itself under the number. */
  footnote?: string
  dotColor?: string
  selected?: boolean
  onClick?: () => void
  /** Where the ⋯ was clicked, so the menu opens under it. */
  onMenu?: (x: number, y: number) => void
  loading?: boolean
  /**
   * How much card there is room for — see `use-strip.ts`. The card does not
   * decide this; the strip measures the row and tells every card at once, so
   * they degrade together instead of one at a time.
   */
  density?: CardDensity
}

export function StatCard({
  definition, result, unit = 'number', footnote, dotColor,
  selected, onClick, onMenu, loading, density = 'full',
}: StatCardProps) {
  const value = result?.value ?? 0
  const comparison = result?.comparison
  const tint = dotColor ?? 'rgb(var(--color-accent))'

  if (density === 'chip') {
    return (
      <StatChip
        definition={definition} value={value} unit={unit} tint={tint}
        loading={loading} selected={selected} onClick={onClick} onMenu={onMenu}
      />
    )
  }

  const picture = density === 'full' || density === 'compact'
  const context = contextLine(definition, result, unit)

  return (
    <Card
      tone={selected ? 'ok' : 'plain'}
      className={cn(
        'relative flex w-full flex-col',
        density === 'full' ? 'p-[18px] pb-3.5' : 'p-3 pb-2.5',
        selected && 'ring-2 ring-accent-ring',
        // A ring rather than a heavier border: `cn()` joins, so a border class
        // here would sit alongside the Card's own and let the stylesheet
        // decide which one a person sees.
        onClick && 'cursor-pointer hover:ring-1 hover:ring-line-strong',
      )}
    >
      {/*
        * The whole card is the button.
        *
        * It used to be only the figure, so clicking the card's *name* — the
        * thing a person points at when they mean "show me these" — did
        * nothing. The ⋯ sits outside it and above it, because a button inside
        * a button is markup browsers resolve however they like.
        */}
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={cn('flex w-full flex-col text-left', density === 'full' ? 'gap-3' : 'gap-2')}
      >
        <span className="flex items-center gap-2 pr-5">
          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: tint }} />
          <span className="min-w-0 flex-1 truncate text-cell font-semibold text-text-muted">
            {definition.label}
          </span>
        </span>

        {/*
          * The figure and its picture, side by side and sitting on the same
          * baseline — which is what the mockup draws and what makes a strip of
          * cards read as one row. They were stacked, so every card was a number
          * with a chart underneath it and the strip was twice as tall as it
          * needed to be.
          */}
        <span className="flex w-full items-end gap-3">
          <span className="flex min-w-0 flex-col">
            <span className={cn('text-stat leading-none text-text', loading && 'opacity-40')}>
              {loading ? '—' : formatCompact(value, unit)}
            </span>
            <span
              className={cn(
                'mt-1 text-cell text-text-muted',
                density === 'full' ? 'line-clamp-2' : 'truncate',
              )}
            >
              {context || '\u00a0'}
            </span>
          </span>

          {/*
            * The picture is the first thing to go. It is the widest part of a
            * card and the part a person can do without: the figure and the
            * line under it say the same thing in words.
            */}
          {picture && (
            <span className="ml-auto shrink-0">
              <Display
                definition={definition} result={result} tint={tint}
                width={density === 'full' ? WIDTH : COMPACT_WIDTH}
              />
            </span>
          )}
        </span>

        {footnote && density === 'full' && (
          <span className="w-full border-t border-line-soft pt-2.5 text-rowsub text-text-faint">
            {footnote}
            {selected && <span className="text-accent"> · click to clear</span>}
          </span>
        )}
      </button>

      {onMenu && (
        <button
          type="button"
          aria-label={`${definition.label} card menu`}
          onClick={(event) => {
            event.stopPropagation()
            const box = event.currentTarget.getBoundingClientRect()
            onMenu(box.left, box.bottom + 4)
          }}
          className={cn(
            'absolute right-3 rounded-field p-0.5 text-text-faint hover:bg-line-soft hover:text-text',
            density === 'full' ? 'top-3.5' : 'top-2',
          )}
        >
          <IconMore size={14} />
        </button>
      )}

      {/* The figure and its change, for anyone not looking at the chart. */}
      {comparison && (
        <span className="sr-only">
          {comparison.changePct === null
            ? 'no comparison figure'
            : `${comparison.changePct > 0 ? 'up' : 'down'} ${Math.abs(comparison.changePct)}%`}
        </span>
      )}
    </Card>
  )
}

/**
 * The last thing a card can be and still be a card.
 *
 * A colour, a name and a number, on one line. No chart, no context, no
 * footnote — and no ⋯ inside a button, so the menu moves to a right-click,
 * which is where the rest of the list engine already puts it.
 */
function StatChip({
  definition, value, unit, tint, loading, selected, onClick, onMenu,
}: {
  definition: CardDefinition
  value: number
  unit: 'money' | 'number'
  tint: string
  loading?: boolean
  selected?: boolean
  onClick?: () => void
  onMenu?: (x: number, y: number) => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      disabled={!onClick}
      title={definition.label}
      onContextMenu={onMenu && ((event) => {
        event.preventDefault()
        onMenu(event.clientX, event.clientY)
      })}
      className={cn(
        'flex h-full w-full items-center gap-2 rounded-card border px-2.5 py-2 text-left',
        selected
          ? 'border-accent-line bg-accent-soft ring-2 ring-accent-ring'
          : 'border-line bg-surface',
        onClick && !selected && 'hover:bg-hover',
      )}
    >
      <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: tint }} />
      <span className="min-w-0 flex-1 truncate text-cell font-semibold text-text-muted">
        {definition.label}
      </span>
      <span className={cn('tnum shrink-0 text-kpi text-text', loading && 'opacity-40')}>
        {loading ? '—' : formatCompact(value, unit)}
      </span>
    </button>
  )
}

/** The line under the number: what period, and what it is being compared with. */
function contextLine(
  definition: CardDefinition, result: CardResult | null, unit: 'money' | 'number',
): string {
  const change = result?.comparison
  const parts: string[] = []

  if (definition.time.period !== 'all_time') {
    parts.push(CARD_PERIOD_LABELS[definition.time.period])
  }
  if (change) {
    parts.push(definition.compare.kind === 'last_year'
      ? `last year ${formatCompact(change.value, unit)}`
      : `before ${formatCompact(change.value, unit)}`)
  }
  if (result && definition.measure.fn !== 'count' && result.records > 0) {
    parts.push(`across ${formatNumber(result.records)}`)
  }

  return parts.join(' · ')
}

function Display({
  definition, result, tint, width = WIDTH,
}: {
  definition: CardDefinition; result: CardResult | null; tint: string; width?: number
}) {
  if (!result) return null

  if (definition.display === 'target') {
    return <Progress value={result.value} target={result.target ?? 0} tint={tint} width={width} />
  }
  if (definition.display === 'trend' && result.series) {
    return (
      <Sparkline
        series={result.series} ghost={result.comparison?.series} tint={tint} width={width} />
    )
  }
  if (definition.display === 'bars' && result.series) {
    return (
      <MiniBars
        series={result.series} ghost={result.comparison?.series} tint={tint} width={width} />
    )
  }

  // Number: the change, as an arrow and a percentage over what it is against.
  const change = result.comparison
  if (!change || change.changePct === null) return null

  const up = change.changePct >= 0
  return (
    <span className="flex flex-col items-end gap-1">
      <span className={cn(
        'tnum flex items-center gap-0.5 text-rowsub font-semibold',
        up ? 'text-accent' : 'text-danger',
      )}>
        {up ? '↑' : '↓'} {Math.abs(change.changePct)}%
      </span>
      <span className="text-sub text-text-faint">
        {definition.compare.kind === 'last_year' ? 'vs last year' : 'vs before'}
      </span>
    </span>
  )
}

/* The mockup's chart box: 140×40 beside the figure, 44px of height either way. */
const WIDTH = 140
/* The same picture with its detail intact, for a card that has lost its padding. */
const COMPACT_WIDTH = 84
const HEIGHT = 40

/** The scale both a series and its comparison share, so the two are comparable. */
function scaleOf(series: { value: number }[], ghost?: { value: number }[]): number {
  const values = [...series.map((point) => point.value), ...(ghost ?? []).map((p) => p.value), 0]
  const top = Math.max(...values)
  const bottom = Math.min(...values)
  // A flat series still needs a range, or every point divides by zero.
  return top === bottom ? 1 : top - bottom
}

function Sparkline({
  series, ghost, tint, width = WIDTH,
}: {
  series: { bucket: string; value: number }[]
  ghost?: { bucket: string; value: number }[]
  tint: string
  width?: number
}) {
  if (series.length < 2) return null

  const values = [...series.map((p) => p.value), ...(ghost ?? []).map((p) => p.value), 0]
  const bottom = Math.min(...values)
  const range = scaleOf(series, ghost)
  // 2px of headroom top and bottom, so the end dot is not clipped.
  const plot = HEIGHT - 4

  const path = (points: { value: number }[]) => points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * width
      const y = 2 + plot - ((point.value - bottom) / range) * plot
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const last = series[series.length - 1]!

  return (
    <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} aria-hidden
      className="block overflow-visible">
      {/* Last year, behind and quiet. */}
      {ghost && ghost.length > 1 && (
        <path d={path(ghost)} fill="none" stroke="rgb(var(--color-line-strong))" strokeWidth="1.5" />
      )}
      <path
        d={`${path(series)} L${width},${HEIGHT} L0,${HEIGHT} Z`}
        fill={tint}
        fillOpacity="0.12"
        stroke="none"
      />
      <path d={path(series)} fill="none" stroke={tint} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={width}
        cy={2 + plot - ((last.value - bottom) / range) * plot}
        r="3"
        fill={tint}
      />
    </svg>
  )
}

function MiniBars({
  series, ghost, tint, width = WIDTH,
}: {
  series: { bucket: string; value: number }[]
  ghost?: { bucket: string; value: number }[]
  tint: string
  width?: number
}) {
  if (series.length === 0) return null

  const range = scaleOf(series, ghost)
  const slot = width / series.length
  const bar = Math.max(2, slot - 2)

  return (
    <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} aria-hidden
      className="block">
      {series.map((point, index) => {
        const last = ghost?.[index]?.value ?? 0
        const x = index * slot
        const height = Math.max(1, (point.value / range) * HEIGHT)
        const ghostHeight = Math.max(0, (last / range) * HEIGHT)
        return (
          <g key={point.bucket}>
            {/* Grey is last year, behind. */}
            {ghostHeight > 0 && (
              <rect x={x} y={HEIGHT - ghostHeight} width={bar} height={ghostHeight}
                rx="1.5" fill="rgb(var(--color-avatar))" />
            )}
            <rect
              x={x} y={HEIGHT - height} width={bar} height={height} rx="1.5"
              fill={tint}
              // The last bucket is the one being reported, so it leads.
              fillOpacity={index === series.length - 1 ? 1 : 0.55}
            />
          </g>
        )
      })}
    </svg>
  )
}

function Progress({
  value, target, tint, width = WIDTH,
}: { value: number; target: number; tint: string; width?: number }) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0
  return (
    <span className="flex flex-col items-end gap-1.5" style={{ width }}>
      <span className="block h-1.5 w-full overflow-hidden rounded-full bg-line-soft">
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: tint }} />
      </span>
      <span className="tnum text-sub text-text-faint">
        {pct}% of {formatNumber(target)}
      </span>
    </span>
  )
}

/**
 * The dashed tile at the end of the strip.
 *
 * It shrinks with everything else and is never dropped: a strip you cannot add
 * to is a strip that gets narrower and stays that way. Below `full` it turns
 * into one line, and at `chip` it is the `+` alone.
 */
export function NewCardTile({
  onClick, density = 'full',
}: { onClick: () => void; density?: CardDensity }) {
  const stacked = density === 'full'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="New card"
      title="New card"
      className={cn(
        'flex shrink-0 items-center justify-center gap-1 rounded-card',
        'border border-dashed border-line-strong text-text-muted',
        'hover:border-accent-line hover:text-accent',
        /*
         * One padding, chosen once. At `chip` — which is what a phone gets —
         * this is 36px wide, the same column as the toolbar's buttons under
         * it, and as tall as the cards beside it, which the strip's
         * `items-stretch` gives it. Written as a second `px-0` after the
         * `px-3` above it, it lost: `cn` joins rather than merges, so both
         * landed and source order kept the 12px, which squashed the glyph.
         */
        density === 'chip'
          ? 'w-9 px-0 py-0'
          : stacked ? 'min-w-[148px] flex-col px-4 py-[18px]' : 'px-3 py-2',
      )}
    >
      <IconPlus size={density === 'chip' ? 18 : 16} />
      {density !== 'chip' && <span className="text-cell">New card</span>}
    </button>
  )
}

/** Which icon a field's measure wears in the builder. */
export const MEASURE_ICONS: Record<string, FieldIcon> = {
  count: 'count',
  sum: 'money',
  avg: 'percent',
}
